import { Button } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAddWishlistProductMutation } from '../mutations/__generated__/addWishlistProduct.generated';
import { useRemoveWishlistProductMutation } from '../mutations/__generated__/removeWishlistProduct.generated';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { GetWishlistProductsDocument } from '../queries/__generated__/getWishlistProducts.generated';
import { RoutePaths } from '../routes/AppRoutes';
import { currentContextAtom } from '../state';
import { wishlistItemsAtom } from './state';
import { storeWishlistActivity } from './wishlist.utils';

export interface UseWishlistProductResult {
  isInWishlist: boolean;
  isLoading: boolean;
  toggleFavorite: () => Promise<void>;
}

export default function useWishlistProduct(
  supplierId: string,
  productSku: number,
): UseWishlistProductResult {
  const matchWishlistPage = useMatch(RoutePaths.WISHLIST);
  const [addWishlistProduct] = useAddWishlistProductMutation({
    refetchQueries: [GetWishlistProductsDocument],
    awaitRefetchQueries: true,
  });
  const [removeWishlistProduct] = useRemoveWishlistProductMutation({
    refetchQueries: [GetWishlistProductsDocument],
    awaitRefetchQueries: true,
  });
  const { t } = useTranslation(['common', 'wishlist']);
  const { roleAssignmentId, companyId } = useAtomValue(currentContextAtom);
  const wishlistItems = useAtomValue(wishlistItemsAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [isLoading, setIsLoading] = useState(false);

  const isInWishlist = wishlistItems.some(
    (item) => item.supplier_id === supplierId && item.sku === productSku,
  );

  const snackbarAction = useMemo((): React.JSX.Element => {
    if (matchWishlistPage) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    return (
      <Button disableRipple variant="text" component={Link} to={RoutePaths.WISHLIST}>
        {t('common:see')}
      </Button>
    )
  }, [matchWishlistPage]);

  const toggleFavorite = async (): Promise<void> => {
    const variables = {
      companyId: companyId!,
      role_assignment_id: roleAssignmentId!,
      supplier_id: supplierId,
      sku: productSku,
    };

    setIsLoading(true);

    if (isInWishlist) {
      const { errors } = await removeWishlistProduct({ variables });

      if (errors) {
        setCommonSnackbarProps({
          label: t('wishlist:error_when_updating_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      } else {
        storeWishlistActivity(roleAssignmentId!);
        setCommonSnackbarProps({
          label: t('wishlist:product_removed_from_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            action: snackbarAction,
          },
        });
      }
    } else {
      const { errors } = await addWishlistProduct({ variables });

      if (errors) {
        setCommonSnackbarProps({
          label: t('wishlist:error_when_updating_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      } else {
        storeWishlistActivity(roleAssignmentId!);
        setCommonSnackbarProps({
          label: t('wishlist:product_added_to_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            action: snackbarAction,
          },
        });
      }
    }

    setIsLoading(false);
  };

  return {
    isInWishlist,
    isLoading,
    toggleFavorite,
  };
}
